import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import InspectionTemplatePanel from './InspectionTemplatePanel';

const InspectionCategoryPanel = ({
  category, title, icon, data, onUpdate, isExpanded, onToggle,
  templates = [], companyId = null,
  dbCategory = null,
  onScheduleAppointment = null,
  vehicleInfo = null
}) => {
  const [showTemplates, setShowTemplates] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleMonths, setScheduleMonths] = useState(3);
  const [scheduleNotes, setScheduleNotes] = useState('');

  const statusOptions = [
    { value: '', label: 'Seleccionar estado' },
    { value: 'bueno', label: 'Bueno' },
    { value: 'regular', label: 'Regular' },
    { value: 'malo', label: 'Malo' },
    { value: 'critico', label: 'Crítico' }
  ];

  const statusColors = { bueno: 'text-success', regular: 'text-warning', malo: 'text-error', critico: 'text-destructive' };

  const handleFieldChange = (field, value) => { onUpdate(category, { ...data, [field]: value }); };

  // ── Parts (multi-row) helpers ──────────────────────────────────────────────
  const getPartsRequired = () => {
    // Migrate legacy single-row refaccion to array if needed
    if (Array.isArray(data?.partsRequired)) return data?.partsRequired;
    if (data?.refaccion) return [{ id: `legacy-0`, description: data?.refaccion, quantity: data?.cantidad || 1 }];
    return [];
  };

  const handleAddPart = () => {
    const current = getPartsRequired();
    handleFieldChange('partsRequired', [...current, { id: `part-${Date.now()}`, description: '', quantity: 1 }]);
  };

  const handleRemovePart = (partId) => {
    handleFieldChange('partsRequired', getPartsRequired()?.filter(p => p?.id !== partId));
  };

  const handlePartChange = (partId, field, value) => {
    handleFieldChange('partsRequired', getPartsRequired()?.map(p =>
      p?.id === partId ? { ...p, [field]: value } : p
    ));
  };

  const handleAddSuggestedPart = (part) => {
    const current = getPartsRequired();
    // Avoid duplicates
    const alreadyAdded = current?.some(p => p?.description?.toLowerCase() === part?.name?.toLowerCase());
    if (alreadyAdded) return;
    handleFieldChange('partsRequired', [
      ...current,
      { id: `sugg-${Date.now()}-${Math.random()}`, description: part?.name || '', quantity: part?.qty || 1 }
    ]);
  };
  // ──────────────────────────────────────────────────────────────────────────

  const handlePhotoUpload = (e) => {
    const files = Array.from(e?.target?.files);
    const newPhotos = files?.map(file => ({
      id: `photo-${Date.now()}-${Math.random()}`,
      url: URL.createObjectURL(file),
      alt: `Evidencia fotográfica de ${title?.toLowerCase()} - ${file?.name}`,
      name: file?.name,
      uploadedAt: new Date()?.toISOString()
    }));
    handleFieldChange('photos', [...(data?.photos || []), ...newPhotos]);
  };

  const handleRemovePhoto = (photoId) => {
    handleFieldChange('photos', data?.photos?.filter(p => p?.id !== photoId));
  };

  const handleApplyTemplate = (templateData) => {
    const newParts = (templateData?.parts || [])?.map((p, i) => ({
      id: `tpl-${Date.now()}-${i}`,
      description: p?.name || '',
      quantity: p?.qty || 1
    }));
    onUpdate(category, {
      ...data,
      status: templateData?.status || data?.status,
      findings: templateData?.findings || data?.findings,
      recommendations: templateData?.recommendations || data?.recommendations,
      parts: templateData?.parts || data?.parts || [],
      labor: templateData?.labor || data?.labor || [],
      partsRequired: newParts?.length > 0 ? newParts : getPartsRequired()
    });
  };

  const handleScheduleConfirm = () => {
    const fechaHora = new Date();
    fechaHora?.setMonth(fechaHora?.getMonth() + (scheduleMonths || 3));
    onScheduleAppointment?.({
      rubro: category,
      rubroLabel: title,
      fechaHora: fechaHora?.toISOString(),
      motivo: `Próxima revisión de ${title}`,
      notas: scheduleNotes
    });
    setShowScheduleModal(false);
    setScheduleNotes('');
    setScheduleMonths(3);
  };

  const isBadCondition = data?.status === 'malo' || data?.status === 'critico';
  const isComplete = data?.status && data?.findings && data?.recommendations && data?.photos?.length > 0;
  const partsRequired = getPartsRequired();

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-1 overflow-hidden transition-smooth">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-4 md:p-6 hover:bg-muted/50 transition-smooth press-effect focus-ring">
        <div className="flex items-center gap-3 md:gap-4">
          <div className={`p-2 md:p-3 rounded-lg ${data?.status ? 'bg-primary/10' : 'bg-muted'}`}>
            <Icon name={icon} size={24} className={data?.status ? statusColors?.[data?.status] || 'text-primary' : 'text-muted-foreground'} />
          </div>
          <div className="text-left">
            <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">{title}</h3>
            <p className="text-xs md:text-sm caption text-muted-foreground">{isComplete ? 'Inspección completa' : 'Pendiente de completar'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 md:gap-3">
          {isBadCondition && (
            <div className="px-2 py-0.5 rounded-full bg-error/10 border border-error/20">
              <span className="text-xs font-medium text-error">Requiere atención</span>
            </div>
          )}
          {isComplete && (<div className="p-1.5 md:p-2 rounded-full bg-success/10"><Icon name="CheckCircle2" size={16} className="text-success" /></div>)}
          <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={20} className="text-muted-foreground" />
        </div>
      </button>
      {isExpanded && (
        <div className="p-4 md:p-6 border-t border-border space-y-4 md:space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <div className="space-y-4">
              <Select
                label="Estado de la inspección"
                description="Seleccione el estado actual del componente"
                required
                options={statusOptions}
                value={data?.status || ''}
                onChange={(value) => handleFieldChange('status', value)}
                error={!data?.status ? 'Campo requerido' : ''}
              />

              <div className="relative">
                <Button variant="outline" iconName="Zap" iconPosition="left" onClick={() => setShowTemplates(!showTemplates)} fullWidth className="border-primary/30 text-primary hover:bg-primary/5">
                  Aplicar plantilla rápida
                </Button>
                <InspectionTemplatePanel
                  category={dbCategory || category}
                  companyId={companyId}
                  onApplyTemplate={handleApplyTemplate}
                  isVisible={showTemplates}
                  onClose={() => setShowTemplates(false)}
                />
              </div>

              <div>
                <label className="block text-sm font-body font-medium text-foreground mb-2">Hallazgos detectados <span className="text-error">*</span></label>
                <textarea value={data?.findings || ''} onChange={(e) => handleFieldChange('findings', e?.target?.value)} placeholder="Describa detalladamente los hallazgos encontrados durante la inspección..." rows={5} className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-input bg-background text-foreground text-sm md:text-base font-body focus:outline-none focus:ring-2 focus:ring-ring transition-smooth resize-none" required />
                {!data?.findings && <p className="text-xs caption text-error mt-1">Campo requerido</p>}
              </div>

              {/* ── Refacciones requeridas (multi-row) ── */}
              <div className="p-3 rounded-lg bg-muted/30 border border-border/60 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                    <Icon name="Package" size={12} />
                    Refacciones requeridas
                  </p>
                  <button
                    type="button"
                    onClick={handleAddPart}
                    className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 transition-smooth px-2 py-1 rounded-md hover:bg-primary/5"
                  >
                    <Icon name="Plus" size={13} />
                    Agregar refacción
                  </button>
                </div>

                {partsRequired?.length === 0 && (
                  <p className="text-xs text-muted-foreground italic text-center py-1">Sin refacciones. Haz clic en "Agregar refacción" o selecciona una pieza sugerida.</p>
                )}

                {partsRequired?.map((part, idx) => (
                  <div key={part?.id} className="flex items-center gap-2">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={part?.description || ''}
                        onChange={(e) => handlePartChange(part?.id, 'description', e?.target?.value)}
                        placeholder={`Ej: Pastillas de freno delanteras`}
                        className="w-full px-2 py-1.5 rounded-md border border-input bg-background text-foreground text-sm font-body focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
                      />
                    </div>
                    <div className="w-16 shrink-0">
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={part?.quantity || 1}
                        onChange={(e) => handlePartChange(part?.id, 'quantity', Number(e?.target?.value) || 1)}
                        className="w-full px-2 py-1.5 rounded-md border border-input bg-background text-foreground text-sm font-body focus:outline-none focus:ring-2 focus:ring-ring transition-smooth text-center"
                        title="Cantidad"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemovePart(part?.id)}
                      className="p-1.5 rounded-md text-muted-foreground hover:text-error hover:bg-error/10 transition-smooth shrink-0"
                      title="Eliminar refacción"
                    >
                      <Icon name="Trash2" size={14} />
                    </button>
                  </div>
                ))}

                {/* Mano de obra */}
                <div className="pt-1 border-t border-border/40">
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={data?.requiereManoObra || false}
                        onChange={(e) => handleFieldChange('requiereManoObra', e?.target?.checked)}
                        className="w-4 h-4 rounded border-input text-primary focus:ring-ring"
                      />
                      <span className="text-xs font-medium text-foreground">¿Requiere mano de obra?</span>
                    </label>
                    {data?.requiereManoObra && (
                      <div className="flex items-center gap-1.5">
                        <label className="text-xs text-muted-foreground">Horas est.:</label>
                        <input
                          type="number"
                          min="0.5"
                          step="0.5"
                          value={data?.horasEstimadas || ''}
                          onChange={(e) => handleFieldChange('horasEstimadas', Number(e?.target?.value) || null)}
                          placeholder="1.5"
                          className="w-16 px-2 py-1 rounded-md border border-input bg-background text-foreground text-xs font-body focus:outline-none focus:ring-2 focus:ring-ring"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-body font-medium text-foreground">Recomendaciones <span className="text-error">*</span></label>
                  {onScheduleAppointment && (
                    <button
                      type="button"
                      onClick={() => setShowScheduleModal(true)}
                      className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-smooth"
                    >
                      <Icon name="CalendarPlus" size={13} />
                      Agendar revisión
                    </button>
                  )}
                </div>
                <textarea value={data?.recommendations || ''} onChange={(e) => handleFieldChange('recommendations', e?.target?.value)} placeholder="Indique las recomendaciones de reparación o mantenimiento necesarias..." rows={5} className="w-full px-3 md:px-4 py-2 md:py-3 rounded-lg border border-input bg-background text-foreground text-sm md:text-base font-body focus:outline-none focus:ring-2 focus:ring-ring transition-smooth resize-none" required />
                {!data?.recommendations && <p className="text-xs caption text-error mt-1">Campo requerido</p>}
              </div>

              {/* Piezas sugeridas — seleccionables */}
              {data?.parts?.length > 0 && (
                <div>
                  <label className="block text-sm font-body font-medium text-foreground mb-2">
                    <Icon name="Package" size={14} className="inline mr-1 text-primary" />
                    Piezas sugeridas — clic para agregar
                  </label>
                  <div className="space-y-1.5">
                    {data?.parts?.map((part, idx) => {
                      const alreadyAdded = partsRequired?.some(
                        p => p?.description?.toLowerCase() === part?.name?.toLowerCase()
                      );
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleAddSuggestedPart(part)}
                          disabled={alreadyAdded}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-sm transition-smooth text-left ${
                            alreadyAdded
                              ? 'bg-success/10 border-success/30 text-success cursor-default' :'bg-muted/30 border-border hover:bg-primary/5 hover:border-primary/30 cursor-pointer'
                          }`}
                        >
                          <span className="font-body text-foreground">{part?.name}</span>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="caption text-muted-foreground">x{part?.qty || 1} {part?.unit || 'pza'}</span>
                            {alreadyAdded
                              ? <Icon name="CheckCircle2" size={14} className="text-success" />
                              : <Icon name="PlusCircle" size={14} className="text-primary" />
                            }
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-body font-medium text-foreground mb-2">Evidencia fotográfica <span className="text-error">*</span></label>
                <div className="border-2 border-dashed border-border rounded-lg p-4 md:p-6 text-center hover:border-primary transition-smooth">
                  <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" id={`photo-upload-${category}`} />
                  <label htmlFor={`photo-upload-${category}`} className="cursor-pointer flex flex-col items-center gap-2">
                    <Icon name="Upload" size={32} className="text-muted-foreground" />
                    <p className="text-sm font-body text-foreground">Arrastra fotos aquí o haz clic para seleccionar</p>
                    <p className="text-xs caption text-muted-foreground">Formatos: JPG, PNG (máx. 5MB por archivo)</p>
                  </label>
                </div>
                {!data?.photos?.length && <p className="text-xs caption text-error mt-1">Se requiere al menos una foto</p>}
              </div>
            </div>
          </div>

          {data?.photos?.length > 0 && (
            <div>
              <h4 className="text-sm font-body font-medium text-foreground mb-3">Fotos adjuntas ({data?.photos?.length})</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
                {data?.photos?.map((photo) => (
                  <div key={photo?.id} className="relative group">
                    <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                      <Image src={photo?.url} alt={photo?.alt} className="w-full h-full object-cover" />
                    </div>
                    <button onClick={() => handleRemovePhoto(photo?.id)} className="absolute top-2 right-2 p-1.5 rounded-full bg-error text-error-foreground opacity-0 group-hover:opacity-100 transition-smooth press-effect focus-ring shadow-elevation-2" title="Eliminar foto">
                      <Icon name="X" size={16} />
                    </button>
                    <p className="text-xs caption text-muted-foreground mt-1 truncate">{photo?.name}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Schedule Appointment Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowScheduleModal(false)}>
          <div className="bg-card rounded-xl border border-border shadow-elevation-5 p-6 max-w-sm w-full" onClick={e => e?.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4">
              <div className="p-2 rounded-lg bg-primary/10">
                <Icon name="CalendarPlus" size={18} className="text-primary" />
              </div>
              <div>
                <h3 className="text-base font-heading font-semibold text-foreground">Agendar próxima revisión</h3>
                <p className="text-xs text-muted-foreground">{title}</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">¿En cuántos meses?</label>
                <select
                  value={scheduleMonths}
                  onChange={e => setScheduleMonths(Number(e?.target?.value))}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value={1}>1 mes</option>
                  <option value={2}>2 meses</option>
                  <option value={3}>3 meses</option>
                  <option value={6}>6 meses</option>
                  <option value={12}>12 meses</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Notas (opcional)</label>
                <textarea
                  value={scheduleNotes}
                  onChange={e => setScheduleNotes(e?.target?.value)}
                  placeholder={`Notas para la revisión de ${title?.toLowerCase()}...`}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowScheduleModal(false)} fullWidth>Cancelar</Button>
              <Button variant="default" iconName="CalendarPlus" iconPosition="left" onClick={handleScheduleConfirm} fullWidth>Agendar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InspectionCategoryPanel;